import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

const LS_KEY = 'regure_locale_settings';

// ─── ONE RESOLVER, ONE WRITER ─────────────────────────────────────────
//
// This module used to hold a SECOND, wrong answer to "what locale is this?".
// It seeded itself from localStorage spread over a hardcoded Swiss default:
//
//     const DEFAULT_LOCALE = { currency: 'CHF', region: 'CH', ... }
//     return saved ? { ...DEFAULT_LOCALE, ...JSON.parse(saved) } : DEFAULT_LOCALE
//
// and the AUTHORITATIVE resolution — user preference, then tenant config —
// lived in App.js, which wrote window.REGURE_* for formatMoney/formatDate to
// read. Nothing ever wrote the tenant's configuration back into this
// context, so `useLocale().currency` was CHF for every tenant on earth.
//
// That stayed invisible for a long time because all fourteen call sites in
// the app call `useLocale();` bare, purely to re-render when the locale
// changes — none of them read a field off it. The first screen that did
// (the offer drawer, building "Vehicle Value / Sum Insured ({{currency}})")
// asked a Bahraini underwriter for a value in Swiss francs while the premium
// beside it, formatted through formatMoney off the window global, correctly
// read BHD. Two answers, one screen, because there were two resolvers.
//
// So: resolveLocale below is the single resolver, LocaleProvider is the
// single writer of the window globals, and useLocale() returns the same
// answer the formatters use. App.js no longer resolves locale at all.
//
// The Swiss default is gone with it. A default indistinguishable from a
// setting is the exact defect App.js already documented for timezone
// ("Writing a fallback here would reintroduce exactly the defect above") —
// it applied that rule to timezone and region but not to currency, which is
// how CHF became the answer for tenants that had never mentioned it.
// resolveCurrency() in regureStyles states the consequence: an unresolved
// currency renders as bare digits, because a WRONG currency symbol on a
// money figure is worse than no symbol at all.

/** Only what the user actually stored. No defaults — a default that comes
 *  back looking like a stored preference is the whole bug above. */
function readStoredPreferences() {
  try {
    const saved = localStorage.getItem(LS_KEY);
    const parsed = saved ? JSON.parse(saved) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

const pick = (...vals) => {
  for (const v of vals) {
    const s = String(v == null ? '' : v).trim();
    if (s) return s;
  }
  return '';
};

/**
 * THE locale, resolved once from the three inputs that can carry one.
 *
 * Order — freshest expression of intent wins:
 *   1. stored  what Account Settings last wrote to localStorage
 *   2. prefs   the user's saved preference on the User record
 *   3. tenant  TenantConfig.locale — the tenant's configuration
 *
 * (1) and (2) are the SAME preference at two ages. localStorage is written
 * the instant the user presses Save; the user record catches up one PATCH
 * later. Ranking the server copy higher would resolve the OLD currency for
 * that round-trip and the save would visibly fail to take — which is why
 * AccountSettings used to write the window globals itself to paper over it.
 * A stale localStorage cannot outlive its owner: logout calls
 * localStorage.clear(), so the next user on this browser starts from their
 * own record and their own tenant.
 *
 * `currency`, `region` and `timezone` come back EMPTY when nothing
 * resolved. That is a meaningful answer and callers must respect it:
 * render no currency rather than a borrowed one.
 *
 * `dateFormat` and `language` do carry a last resort, because unlike a
 * currency they have no "none" — a date must be written in some order and
 * the interface must be in some language.
 *
 * Pure, and exported, so the resolution can be tested without a store.
 */
export function resolveLocale({ prefs = {}, stored = {}, tenant = {} } = {}) {
  return {
    currency:   pick(stored.currency, prefs.currency, tenant.currency).toUpperCase(),
    dateFormat: pick(stored.dateFormat, prefs.dateFormat, tenant.dateFormat, 'MM/DD/YYYY'),
    language:   pick(stored.language, prefs.language, tenant.language, 'en'),
    region:     pick(stored.region, prefs.region, tenant.country, tenant.region),
    timezone:   pick(stored.timezone, prefs.timezone, tenant.timezone),
  };
}

const EMPTY_LOCALE = resolveLocale();

// The globals the non-React formatters read (formatMoney, formatDate).
// Written in exactly one place — here — and DELETED rather than defaulted
// when nothing resolved, so a stale value from an earlier session or an
// earlier tenant cannot outlive the thing that set it.
function applyToWindow(locale) {
  if (typeof window === 'undefined') return;
  const set = (key, value) => {
    if (value) window[key] = value;
    else delete window[key];
  };
  set('REGURE_CURRENCY', locale.currency);
  set('REGURE_DATE_FMT', locale.dateFormat);
  set('REGURE_REGION', locale.region);
  set('REGURE_TIMEZONE', locale.timezone);
}

export const LocaleContext = createContext(EMPTY_LOCALE);

export function LocaleProvider({ children }) {
  // The tenant's configuration and the user's preferences both live in
  // redux, so the resolution re-runs by itself when tenantConfig hydrates
  // — which a one-shot read of window.REGURE_CURRENCY could never do.
  const tenant = useSelector(state => state?.tenantConfig?.config?.locale) || null;
  const prefs = useSelector(state => state?.userLogin?.userInfo?.data?.user?.preferences) || null;

  // localStorage is not reactive; Account Settings announces its saves.
  const [stored, setStored] = useState(readStoredPreferences);
  useEffect(() => {
    const handler = () => setStored(readStoredPreferences());
    window.addEventListener('regure-locale-changed', handler);
    return () => window.removeEventListener('regure-locale-changed', handler);
  }, []);

  const locale = useMemo(
    () => resolveLocale({ prefs: prefs || {}, stored, tenant: tenant || {} }),
    [prefs, stored, tenant],
  );

  // Effect, not render: writing a global during render makes the first
  // paint depend on render order.
  useEffect(() => { applyToWindow(locale); }, [locale]);

  return (
    <LocaleContext.Provider value={locale}>
      {children}
    </LocaleContext.Provider>
  );
}

/**
 * The resolved locale. Re-renders the component whenever it changes —
 * whether that is the tenant config arriving or the user saving a
 * preference in Account Settings.
 *
 * `currency` is the TENANT's currency (or the user's override), and is ''
 * when nothing has resolved. Build a money label as "Premium" in that case,
 * never "Premium (CHF)".
 */
export function useLocale() {
  return useContext(LocaleContext);
}
